import { yupResolver } from '@hookform/resolvers/yup';
import type { FC } from 'react';
import { useEffect, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { FormSelect, HttpStatus } from '@catalyze/ui';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useOptionsWithSearch } from 'features/SignUp/hooks/useOptionsWithSearch';
import type { ILocationForm } from 'features/SignUp/types/signUpForm';
import { STEP_COUNT } from 'features/SignUp/types/signUpForm';
import { locationSchema } from 'schemas/signUpForm';

import { FormLine, InputContainer, ButtonsContainer } from './styled';

import { StyledForm } from '../../Form';
import { StyledFormButton } from '../../FormButton';
import { FormTitle } from '../../FormTitle';
import { ProgressBar } from '../../ProgressBar';

interface ILocationFormProps {
    initialValues: ILocationForm | undefined;
    onSubmit: (data: ILocationForm) => void;
    onGoBack: (data: ILocationForm) => void;
    loading?: boolean;
}

export const LocationForm: FC<ILocationFormProps> = ({
    onSubmit,
    onGoBack,
    initialValues,
    loading,
}) => {
    const formMethods = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(locationSchema),
    });
    const [searchValue, setSearchValue] = useState('');
    const { getValues, handleSubmit, setError, clearErrors, setValue } =
        formMethods;

    // Limits characters to numbers of max length
    const setValidSearchValue = useCallback(
        (test: string) =>
            setSearchValue(test.replace(/[^0-9]/u, '').slice(0, 5)),
        []
    );
    const { options, error, isLoading, search, foundSearchOption } =
        useOptionsWithSearch({
            search: searchValue,
            prefetchValue: getValues()?.zip || undefined,
        });

    // Sets newly found option as value, reset errors
    useEffect(() => {
        if (foundSearchOption) {
            setValue('zip', foundSearchOption.value);
            setValue('state', foundSearchOption.state);
            clearErrors('zip');
        }
    }, [foundSearchOption, setValue, clearErrors]);

    // Sets error if zip is not found
    useEffect(() => {
        if (error?.response) {
            setError('zip', {
                message:
                    Number(error?.response.status || 0) === HttpStatus.NOT_FOUND
                        ? 'Zip code not found'
                        : 'Error occured, please try again',
            });
        } else {
            clearErrors('zip');
        }
    }, [error, setError, clearErrors]);

    const value = getValues().zip;

    return (
        <>
            <LoadingOverlay shown={loading} />
            <ProgressBar progress={(4 / STEP_COUNT) * 100} />
            <FormTitle
                goBack={() => onGoBack(getValues())}
                title="Your location"
                subtitle="We will provide you with property data within
                a reasonable distance of your brokerage license region."
            />
            <FormProvider {...formMethods}>
                <StyledForm onSubmit={handleSubmit(onSubmit)}>
                    <FormLine>
                        <InputContainer>
                            <FormSelect
                                id="zip"
                                label="Zip code"
                                disabled={loading}
                                options={options}
                                value={value}
                                isCreatable
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                onSelectOption={(value: any) => {
                                    setValue('state', value.state);
                                }}
                                selectProps={{
                                    inputValue: searchValue,
                                    onInputChange: setValidSearchValue,
                                    onCreateOption: (value) => {
                                        search({ code: value });
                                    },
                                    formatCreateLabel: (value) =>
                                        `Search for ${value}`,
                                    isLoading,
                                    isDisabled: isLoading,
                                }}
                            />
                        </InputContainer>
                    </FormLine>
                    <ButtonsContainer>
                        <StyledFormButton type="submit" disabled={loading}>
                            Next
                        </StyledFormButton>
                    </ButtonsContainer>
                </StyledForm>
            </FormProvider>
        </>
    );
};

LocationForm.defaultProps = {
    loading: false,
};
